import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, DollarSign, ExternalLink, CheckCircle2, XCircle, RefreshCcw, FileText, Pencil, CreditCard, HandCoins, Download, Bell, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Inline controls that render on each order row when
 * `statusFilter === "invoiced"` (or the order is in the invoiced state).
 *
 * Show:
 *  - Stripe invoice hosted URL (link out)
 *  - Collect button (off-session charge against saved default card)
 *  - Xero sync indicators — small green pill once synced, no button
 *    (backend auto-syncs after collect / after invoice creation, so admin
 *     usually doesn't need a manual button here). A retry button only
 *    surfaces when `xero_sync_error` is set.
 *  - Last error message (red text) when a previous collect failed.
 */
export default function InvoicedControls({ order, onChanged, xeroPaid }) {
  const [busy, setBusy] = useState(null);
  const [partialOpen, setPartialOpen] = useState(false);
  const [partialAmount, setPartialAmount] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [manualStep, setManualStep] = useState("form"); // "dd-choice" | "form"
  const [chargeAnywayOpen, setChargeAnywayOpen] = useState(false);
  const [manualAmount, setManualAmount] = useState("");
  const [manualNote, setManualNote] = useState("");
  const [xeroStatus, setXeroStatus] = useState(null);

  const paymentMode = order.hotel_payment_mode || "direct_debit";
  const isManualMode = paymentMode === "manual_invoice";

  useEffect(() => {
    if (!manualOpen) return;
    setXeroStatus(null);
    axios.get(`${API}/billing/orders/${order.id}/xero-status`)
      .then((r) => setXeroStatus(r.data))
      .catch(() => setXeroStatus({ error: "lookup failed" }));
  }, [manualOpen]);

  const collect = async (force = false) => {
    setBusy("collecting");
    try {
      const res = await axios.post(`${API}/billing/orders/${order.id}/collect`, force ? { force: true } : {});
      if (res.data?.ok) {
        toast.success(`Collected $${res.data.amount_paid?.toFixed(2)}`);
      } else {
        toast.error(res.data?.error || "Card declined");
      }
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Charge failed");
    } finally {
      setBusy(null);
    }
  };

  const retryXero = async (kind) => {
    setBusy(`xero_${kind}`);
    try {
      await axios.post(`${API}/xero/orders/${order.id}/sync-${kind}`);
      toast.success(`Xero ${kind} synced`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Xero ${kind} failed`);
    } finally {
      setBusy(null);
    }
  };

  const collected = order.billing_status === "collected";
  const failed = order.billing_status === "failed";
  const hasStripeInvoice = !!order.stripe_invoice_id;
  // No-card hotels are invoiced in Xero only — there is deliberately NO
  // Stripe invoice object for them, so gate on billing_status, not on the
  // presence of a Stripe id.
  const isInvoiced = hasStripeInvoice || ["invoiced", "failed", "collected"].includes(order.billing_status);

  const createInvoice = async () => {
    setBusy("creating");
    try {
      await axios.post(`${API}/billing/orders/${order.id}/create-invoice`);
      toast.success("Stripe invoice created");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create invoice");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`invoiced-controls-${order.id}`}>
      {/* Live 'paid in Xero' badge — visible on the invoice ROW itself, no
          need to open the manual dialog. */}
      {!collected && xeroPaid && (xeroPaid.amount_paid > 0 || xeroPaid.fully_paid) && (
        <span
          className="inline-flex items-center gap-1 text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-300 rounded-sm px-1.5 py-0.5"
          data-testid={`xero-paid-badge-${order.id}`}
          title={`Xero shows $${(xeroPaid.amount_paid || 0).toFixed(2)} received${xeroPaid.fully_paid ? " — fully paid" : ` (due $${(xeroPaid.amount_due || 0).toFixed(2)})`}`}
        >
          <CheckCircle2 className="w-3 h-3" />
          {xeroPaid.fully_paid ? "Paid in Xero" : `Xero rec'd $${(xeroPaid.amount_paid || 0).toFixed(2)}`}
        </span>
      )}
      {/* If the order was flipped to invoiced BEFORE Stripe was wired up (or
          the auto-hook failed), let the operator create the invoice on-demand.
          Once created, the Collect / manual-payment controls appear. */}
      {!isInvoiced && (
        <Button
          size="sm"
          variant="outline"
          onClick={createInvoice}
          disabled={busy === "creating"}
          className="h-7 px-2 text-[11px] rounded-sm border-purple-300 text-purple-800 hover:bg-purple-50"
          data-testid={`create-invoice-${order.id}`}
        >
          {busy === "creating" ? <Loader2 className="w-3 h-3 animate-spin" /> : <><FileText className="w-3 h-3 mr-0.5" />Create invoice</>}
        </Button>
      )}
      {/* Stripe hosted invoice link (view / customer-facing) */}
      {order.stripe_invoice_hosted_url && (
        <a
          href={order.stripe_invoice_hosted_url}
          target="_blank"
          rel="noreferrer"
          className="text-[11px] text-brand-navy hover:underline flex items-center gap-1"
          data-testid={`stripe-hosted-${order.id}`}
        >
          <ExternalLink className="w-3 h-3" /> Stripe
        </a>
      )}

      {/* Xero PDF download — streams the actual invoice PDF straight from
          Xero's API so any manual edit (line-item change, GST tweak,
          branding update) is reflected instantly without a re-sync. */}
      {order.xero_invoice_id && (
        <button
          type="button"
          onClick={async () => {
            try {
              const res = await axios.get(`${API}/billing/orders/${order.id}/invoice.pdf`, { responseType: "blob" });
              const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
              const a = document.createElement("a");
              a.href = url;
              a.download = `${order.order_number || order.id}.pdf`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Xero PDF download failed");
            }
          }}
          className="text-[11px] text-blue-700 hover:underline flex items-center gap-1"
          data-testid={`xero-pdf-${order.id}`}
          title="Download invoice PDF from Xero"
        >
          <Download className="w-3 h-3" /> Xero PDF
        </button>
      )}

      {/* Collect — only when we have an invoice and it's not yet collected */}
      {isInvoiced && !collected && (
        <>
          {(order.partial_payments || []).length > 0 && (() => {
            const paid = (order.partial_payments || []).reduce((s, p) => s + (p.amount || 0), 0);
            const total = Number(order.total_price) || 0;
            const remaining = Math.max(0, total - paid);
            const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
            return (
              <div
                className="flex items-center gap-1.5 text-[11px] text-neutral-700 bg-emerald-50 border border-emerald-200 rounded-sm px-1.5 py-0.5"
                data-testid={`paid-progress-${order.id}`}
                title={`Paid $${paid.toFixed(2)} of $${total.toFixed(2)} — $${remaining.toFixed(2)} remaining`}
              >
                <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                <span>
                  Paid <span className="font-semibold">${paid.toFixed(2)}</span>
                  {" / "}${total.toFixed(2)}
                  <span className="text-neutral-500"> ({pct}%)</span>
                </span>
                <span className="text-red-700 font-semibold ml-1">
                  Remaining ${remaining.toFixed(2)}
                </span>
              </div>
            );
          })()}
          {order.hotel_has_card ? (
            <>
              {isManualMode && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-semibold bg-red-50 text-red-700 border border-red-300 rounded-sm px-1.5 py-0.5"
                  data-testid={`manual-mode-pill-${order.id}`}
                  title="This hotel is set to Manual Invoice — card charging is blocked unless you override"
                >
                  <AlertTriangle className="w-3 h-3" />
                  Manual invoice — charge blocked
                </span>
              )}
              <Button
                size="sm"
                onClick={() => (isManualMode ? setChargeAnywayOpen(true) : collect())}
                disabled={busy === "collecting"}
                className={`${isManualMode ? "bg-neutral-400 hover:bg-neutral-500" : "bg-emerald-600 hover:bg-emerald-700"} text-white rounded-sm h-7 px-2`}
                data-testid={`collect-${order.id}`}
              >
                {busy === "collecting" ? <Loader2 className="w-3 h-3 animate-spin" /> : <><DollarSign className="w-3 h-3 mr-0.5" />{failed ? "Retry" : "Collect"}</>}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  // order.total_price is ALREADY GST-inclusive (computed as subtotal * 1.10
                  // at order creation) — do NOT multiply again.
                  const remaining = (Number(order.total_price) || 0)
                    - (order.partial_payments || []).reduce((s, p) => s + (p.amount || 0), 0);
                  setPartialAmount(remaining > 0 ? remaining.toFixed(2) : "");
                  setPartialOpen(true);
                }}
                className="h-7 px-2 text-[11px] rounded-sm border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                data-testid={`partial-${order.id}`}
                title="Charge a partial amount (customer can't pay in full)"
              >
                <Pencil className="w-3 h-3" />
              </Button>
              {/* Manual (off-Stripe) payment — available even when a card is
                  on file, for when the customer pays by bank transfer/cash
                  this time instead of being charged. */}
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const remaining = (Number(order.total_price) || 0)
                    - (order.partial_payments || []).reduce((s, p) => s + (p.amount || 0), 0);
                  setManualAmount(remaining > 0 ? remaining.toFixed(2) : "");
                  setManualStep(paymentMode === "direct_debit" ? "dd-choice" : "form");
                  setManualOpen(true);
                }}
                className="h-7 px-2 text-[11px] rounded-sm border-amber-300 text-amber-800 hover:bg-amber-50"
                data-testid={`manual-collect-${order.id}`}
                title="Record a manual payment (bank transfer / cash) instead of charging the card"
              >
                <HandCoins className="w-3 h-3 mr-0.5" />
                Manual
              </Button>
            </>
          ) : (
            <>
              {/* Hotel has NO card on file — surface a very visible pill
                  telling the operator to reach out to the customer, plus a
                  "Record manual payment" button to log the amount collected
                  (bank transfer / cash / other). The manual amount is pushed
                  to the Xero invoice as a description-only line so the
                  ledger shows what happened and when. */}
              <span
                className="inline-flex items-center gap-1 text-[10px] font-medium bg-amber-100 text-amber-900 border border-amber-300 rounded-sm px-1.5 py-0.5"
                data-testid={`no-card-warning-${order.id}`}
                title="This hotel has no default card on file. Contact them to collect payment manually."
              >
                <CreditCard className="w-3 h-3" />
                No card — contact customer
              </span>
              <Button
                size="sm"
                onClick={() => {
                  const remaining = (Number(order.total_price) || 0)
                    - (order.partial_payments || []).reduce((s, p) => s + (p.amount || 0), 0);
                  setManualAmount(remaining > 0 ? remaining.toFixed(2) : "");
                  setManualStep(paymentMode === "direct_debit" ? "dd-choice" : "form");
                  setManualOpen(true);
                }}
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm h-7 px-2"
                data-testid={`manual-collect-${order.id}`}
                title="Record a manual payment (bank transfer / cash) against this invoice"
              >
                <HandCoins className="w-3 h-3 mr-0.5" />
                Record payment
              </Button>
            </>
          )}
          {/* Overdue reminder — invoice 7+ days old, synced to Xero */}
          {(() => {
            const invAt = order.stripe_invoiced_at;
            const days = invAt ? Math.floor((Date.now() - new Date(invAt).getTime()) / 86400000) : null;
            if (!(order.xero_invoice_id && days != null && days >= 7)) return null;
            return (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy === "reminder"}
                  className="h-7 px-2 text-[11px] rounded-sm border-red-300 text-red-700 hover:bg-red-50"
                  data-testid={`send-reminder-${order.id}`}
                  title={`Invoice is ${days} days old — email an overdue reminder via Xero to the client's login email`}
                  onClick={async () => {
                    if (!window.confirm("Send an overdue reminder email via Xero to this client's login email?")) return;
                    setBusy("reminder");
                    try {
                      const res = await axios.post(`${API}/billing/orders/${order.id}/send-reminder`);
                      toast.success(`Reminder sent via Xero to ${res.data?.sent_to}`);
                      onChanged?.();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Reminder failed");
                    } finally {
                      setBusy(null);
                    }
                  }}
                >
                  {busy === "reminder" ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Bell className="w-3 h-3 mr-0.5" />Remind ({days}d)</>}
                </Button>
                {order.reminder_sent_at && (
                  <span
                    className="text-[10px] text-neutral-500"
                    data-testid={`reminder-sent-${order.id}`}
                    title={`Last reminder sent ${new Date(order.reminder_sent_at).toLocaleString()}`}
                  >
                    reminded {new Date(order.reminder_sent_at).toLocaleDateString()}
                  </span>
                )}
              </>
            );
          })()}
        </>
      )}

      {/* Manual-payment dialog (no card on file) */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-sm" data-testid={`manual-dialog-${order.id}`}>
          {manualStep === "dd-choice" ? (
            <>
              <DialogHeader>
                <DialogTitle>Direct Debit client</DialogTitle>
                <DialogDescription>
                  This client is on <strong>Direct Debit</strong> — payments are normally charged to their saved card.
                  How do you want to record this payment?
                </DialogDescription>
              </DialogHeader>
              <div
                className="text-xs rounded-md px-3 py-2 border bg-amber-50 border-amber-300 text-amber-900 font-medium"
                data-testid={`dd-warning-${order.id}`}
              >
                ⚠ Direct Debit mode — recording a manual payment here bypasses the card charge.
              </div>
              {!xeroStatus && (
                <div className="text-xs text-neutral-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Checking Xero sync…</div>
              )}
              <div className="flex flex-col gap-2 pt-1">
                <Button
                  variant="outline"
                  className="rounded-sm justify-start"
                  disabled={!xeroStatus}
                  data-testid={`dd-xero-${order.id}`}
                  onClick={() => {
                    if (xeroStatus?.linked && xeroStatus?.connected) {
                      toast.info("OK — record the payment in Xero. Once it syncs, it will show as received here.");
                      setManualOpen(false);
                    } else {
                      toast.warning("Xero hasn't synced for this invoice — manual recording is allowed.");
                      setManualStep("form");
                    }
                  }}
                >
                  I'll record it in Xero
                </Button>
                <Button
                  variant="outline"
                  className="rounded-sm justify-start border-amber-300 text-amber-800 hover:bg-amber-50"
                  data-testid={`dd-manual-${order.id}`}
                  onClick={() => setManualStep("form")}
                >
                  Record manually here
                </Button>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setManualOpen(false)} className="rounded-sm">Cancel</Button>
              </DialogFooter>
            </>
          ) : (
            <>
          <DialogHeader>
            <DialogTitle>Record manual payment</DialogTitle>
            <DialogDescription>
              Log an amount collected off-Stripe (bank transfer, cash, etc.).
              Mark the payment as received in Xero first (bank reconciliation) —
              this only updates the billing status here, nothing is pushed to Xero.
            </DialogDescription>
          </DialogHeader>
          {isManualMode && (
            <div
              className="text-xs rounded-md px-3 py-2 border bg-blue-50 border-blue-300 text-blue-900 font-medium"
              data-testid={`xero-managed-notice-${order.id}`}
            >
              This client's account is managed in <strong>Xero</strong> (Manual Invoice mode) — please record the
              payment in Xero. Manual recording here is only for when Xero hasn't synced.
            </div>
          )}
          {xeroStatus && xeroStatus.linked && xeroStatus.connected && !xeroStatus.error && (
            <div
              data-testid={`xero-paid-status-${order.id}`}
              className={`text-xs rounded-md px-3 py-2 border font-medium ${
                xeroStatus.fully_paid
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : xeroStatus.amount_paid > 0
                    ? "bg-amber-50 border-amber-200 text-amber-800"
                    : "bg-red-50 border-red-200 text-red-700"
              }`}
            >
              {xeroStatus.fully_paid
                ? `✓ Received in Xero — $${xeroStatus.amount_paid.toFixed(2)} paid, invoice PAID`
                : xeroStatus.amount_paid > 0
                  ? `Partially received in Xero — $${xeroStatus.amount_paid.toFixed(2)} paid, $${xeroStatus.amount_due.toFixed(2)} still due`
                  : "✗ Not yet marked received in Xero — reconcile the payment in Xero first, then record it here"}
            </div>
          )}
          {xeroStatus && xeroStatus.linked && xeroStatus.connected === false && (
            <div className="text-xs rounded-md px-3 py-2 border bg-neutral-50 text-neutral-600">
              Xero not connected — recording locally only.
            </div>
          )}
          {(order.partial_payments || []).length > 0 && (
            <div className="text-xs bg-neutral-50 rounded-md p-2 space-y-1">
              <div className="font-medium">Previous collections:</div>
              {(order.partial_payments || []).map((p, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-neutral-500">
                    {new Date(p.at).toLocaleDateString()}
                    {p.method === "manual" && <span className="ml-1 text-amber-700">(manual)</span>}
                  </span>
                  <span className="font-mono">${p.amount.toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between border-t pt-1 font-semibold">
                <span>Collected so far</span>
                <span>${(order.partial_payments || []).reduce((s, p) => s + (p.amount || 0), 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-brand-navy font-semibold">
                <span>Invoice total (incl GST)</span>
                <span>${(Number(order.total_price) || 0).toFixed(2)}</span>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Amount ($):</span>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              max={(() => {
                const total = Number(order.total_price) || 0;
                const paid = (order.partial_payments || []).reduce((s, p) => s + (p.amount || 0), 0);
                return Math.max(0, total - paid).toFixed(2);
              })()}
              value={manualAmount}
              onChange={(e) => setManualAmount(e.target.value)}
              data-testid={`manual-amount-${order.id}`}
              autoFocus
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium whitespace-nowrap">Note:</span>
            <Input
              type="text"
              placeholder="e.g. Bank transfer ref 12345 (goes on the Xero invoice)"
              value={manualNote}
              onChange={(e) => setManualNote(e.target.value)}
              data-testid={`manual-note-${order.id}`}
            />
          </div>
          {(() => {
            const total = Number(order.total_price) || 0;
            const paid = (order.partial_payments || []).reduce((s, p) => s + (p.amount || 0), 0);
            const remaining = Math.max(0, total - paid);
            const entered = Number(manualAmount);
            const overCharge = entered > remaining + 0.01;
            return (
              <div className="flex justify-between text-xs">
                <span className="text-neutral-500">Remaining balance</span>
                <span className={overCharge ? "font-semibold text-red-600" : "font-semibold"}>
                  ${remaining.toFixed(2)}
                </span>
              </div>
            );
          })()}
          {(() => {
            const total = Number(order.total_price) || 0;
            const paid = (order.partial_payments || []).reduce((s, p) => s + (p.amount || 0), 0);
            const remaining = Math.max(0, total - paid);
            const entered = Number(manualAmount);
            return entered > remaining + 0.01 ? (
              <div className="text-xs text-red-600 -mt-1" data-testid={`manual-over-warning-${order.id}`}>
                Amount exceeds remaining balance. Max: ${remaining.toFixed(2)}.
              </div>
            ) : null;
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)} className="rounded-sm">Cancel</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
              disabled={(() => {
                if (busy === "manual") return true;
                const total = Number(order.total_price) || 0;
                const paid = (order.partial_payments || []).reduce((s, p) => s + (p.amount || 0), 0);
                const remaining = Math.max(0, total - paid);
                // Zero-balance invoice: allow a $0 settlement record.
                if (remaining <= 0.009) return false;
                const entered = Number(manualAmount);
                if (!manualAmount || entered <= 0) return true;
                return entered > remaining + 0.01;
              })()}
              data-testid={`manual-confirm-${order.id}`}
              onClick={async () => {
                setBusy("manual");
                try {
                  const res = await axios.post(`${API}/billing/orders/${order.id}/manual-collect`, {
                    amount: Number(manualAmount),
                    note: manualNote.trim(),
                  });
                  if (res.data?.ok) {
                    toast.success(
                      `Recorded $${res.data.amount.toFixed(2)}` +
                      (res.data.fully_paid ? " — invoice fully paid" : ` (${res.data.total_collected.toFixed(2)}/${res.data.invoice_total.toFixed(2)})`)
                    );
                    if (res.data.xero_warning) toast.warning(res.data.xero_warning, { duration: 9000 });
                    setManualOpen(false);
                    setManualNote("");
                    onChanged?.();
                  } else {
                    toast.error(res.data?.error || "Recording failed");
                  }
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Recording failed");
                } finally {
                  setBusy(null);
                }
              }}
            >
              {busy === "manual" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Record"}
            </Button>
          </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Charge-anyway confirm (manual invoice mode) */}
      <Dialog open={chargeAnywayOpen} onOpenChange={setChargeAnywayOpen}>
        <DialogContent className="max-w-sm" data-testid={`charge-anyway-dialog-${order.id}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-4 h-4" /> Charge blocked
            </DialogTitle>
            <DialogDescription>
              This client is set to <strong>Manual Invoice</strong> mode — the system blocks charging their saved card.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs rounded-md px-3 py-2 border bg-red-50 border-red-300 text-red-800 font-medium">
            Only proceed if you are sure this charge is intended. The card will be charged immediately.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChargeAnywayOpen(false)} className="rounded-sm">Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
              disabled={busy === "collecting"}
              data-testid={`charge-anyway-confirm-${order.id}`}
              onClick={async () => { setChargeAnywayOpen(false); await collect(true); }}
            >
              {busy === "collecting" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Charge anyway"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partial-payment dialog */}
      <Dialog open={partialOpen} onOpenChange={setPartialOpen}>
        <DialogContent className="max-w-sm" data-testid={`partial-dialog-${order.id}`}>
          <DialogHeader>
            <DialogTitle>Partial payment</DialogTitle>
            <DialogDescription>
              Charge a partial amount against the saved card. Invoice stays open in Xero until fully paid.
            </DialogDescription>
          </DialogHeader>
          {isManualMode && (
            <div className="text-xs rounded-md px-3 py-2 border bg-red-50 border-red-300 text-red-800 font-medium" data-testid={`partial-manual-warning-${order.id}`}>
              ⚠ This client is on Manual Invoice mode — charging is normally blocked. Confirming here overrides the block.
            </div>
          )}
          {(order.partial_payments || []).length > 0 && (
            <div className="text-xs bg-neutral-50 rounded-md p-2 space-y-1">
              <div className="font-medium">Previous partials:</div>
              {(order.partial_payments || []).map((p, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-neutral-500">{new Date(p.at).toLocaleDateString()}</span>
                  <span className="font-mono">${p.amount.toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between border-t pt-1 font-semibold">
                <span>Collected so far</span>
                <span>${(order.partial_payments || []).reduce((s, p) => s + (p.amount || 0), 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-brand-navy font-semibold">
                <span>Invoice total (incl GST)</span>
                <span>${(Number(order.total_price) || 0).toFixed(2)}</span>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Amount ($):</span>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              max={(() => {
                const total = Number(order.total_price) || 0;
                const paid = (order.partial_payments || []).reduce((s, p) => s + (p.amount || 0), 0);
                return Math.max(0, total - paid).toFixed(2);
              })()}
              value={partialAmount}
              onChange={(e) => setPartialAmount(e.target.value)}
              data-testid={`partial-amount-${order.id}`}
              autoFocus
            />
          </div>
          {(() => {
            const total = Number(order.total_price) || 0;
            const paid = (order.partial_payments || []).reduce((s, p) => s + (p.amount || 0), 0);
            const remaining = Math.max(0, total - paid);
            const entered = Number(partialAmount);
            const overCharge = entered > remaining + 0.01;
            return (
              <div className="flex justify-between text-xs">
                <span className="text-neutral-500">Remaining balance</span>
                <span className={overCharge ? "font-semibold text-red-600" : "font-semibold"}>
                  ${remaining.toFixed(2)}
                </span>
              </div>
            );
          })()}
          {(() => {
            const total = Number(order.total_price) || 0;
            const paid = (order.partial_payments || []).reduce((s, p) => s + (p.amount || 0), 0);
            const remaining = Math.max(0, total - paid);
            const entered = Number(partialAmount);
            return entered > remaining + 0.01 ? (
              <div className="text-xs text-red-600 -mt-1" data-testid={`partial-over-warning-${order.id}`}>
                Amount exceeds remaining balance. Max chargeable: ${remaining.toFixed(2)}.
              </div>
            ) : null;
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPartialOpen(false)} className="rounded-sm">Cancel</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
              disabled={(() => {
                if (busy === "partial") return true;
                const entered = Number(partialAmount);
                if (!partialAmount || entered <= 0) return true;
                const total = Number(order.total_price) || 0;
                const paid = (order.partial_payments || []).reduce((s, p) => s + (p.amount || 0), 0);
                const remaining = Math.max(0, total - paid);
                return entered > remaining + 0.01;
              })()}
              data-testid={`partial-confirm-${order.id}`}
              onClick={async () => {
                setBusy("partial");
                try {
                  const res = await axios.post(`${API}/billing/orders/${order.id}/collect-partial`, {
                    amount: Number(partialAmount),
                    ...(isManualMode ? { force: true } : {}),
                  });
                  if (res.data?.ok) {
                    toast.success(`Charged $${res.data.partial_amount.toFixed(2)}` + (res.data.fully_paid ? " — invoice fully paid" : ` (${res.data.total_collected.toFixed(2)}/${res.data.invoice_total.toFixed(2)})`));
                    setPartialOpen(false);
                    onChanged?.();
                  } else {
                    toast.error(res.data?.error || "Charge failed");
                  }
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Charge failed");
                } finally {
                  setBusy(null);
                }
              }}
            >
              {busy === "partial" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Charge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Collected badge */}
      {collected && (
        <span className="text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5 flex items-center gap-0.5 font-medium">
          <CheckCircle2 className="w-3 h-3" /> Paid
        </span>
      )}

      {/* Failed badge */}
      {failed && (
        <span className="text-[10px] bg-red-100 text-red-800 border border-red-200 rounded-full px-2 py-0.5 flex items-center gap-0.5 font-medium" title={order.stripe_last_error || "Declined"}>
          <XCircle className="w-3 h-3" /> Declined
        </span>
      )}

      {/* Xero indicators — invoice */}
      {order.xero_invoice_id ? (
        <span className="text-[10px] text-blue-700 flex items-center gap-0.5" title={`Xero InvoiceID ${order.xero_invoice_id}`}>
          <CheckCircle2 className="w-3 h-3" /> Xero inv
        </span>
      ) : (
        order.xero_sync_error && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => retryXero("invoice")}
            disabled={busy === "xero_invoice"}
            className="h-7 px-2 text-[11px] text-red-700 border-red-300 rounded-sm"
            data-testid={`retry-xero-inv-${order.id}`}
            title={order.xero_sync_error}
          >
            {busy === "xero_invoice" ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RefreshCcw className="w-3 h-3 mr-0.5" />Xero inv</>}
          </Button>
        )
      )}
      {order.xero_payment_id && (
        <span className="text-[10px] text-blue-700 flex items-center gap-0.5" title={`Xero PaymentID ${order.xero_payment_id}`}>
          <CheckCircle2 className="w-3 h-3" /> Xero paid
        </span>
      )}
    </div>
  );
}
